import { Component, ElementRef, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BaseComponent } from '../../shared/base/base.component';
import { BaseService } from '../../../providers/base.service';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

// Where a dedicate prospect reads their preinvoice and approves it, or rejects
// it with a reason. The page needs no login: it is opened from the link that
// SEND_PREINVOICE_LINK on a dedicate lead texts them, whose token leads back to
// that issue, and the answer goes to api/dedicate/preinvoice/answer.
// The link is opened on a phone, where a pdf does not show inside a page, so the
// pdf is drawn onto canvases with pdf.js across the whole screen, with the answer
// in buttons floating over it; when the pdf cannot be had, the lines are drawn
// here instead.
@Component({
  selector: 'app-dedicate-preinvoice',
  templateUrl: './dedicate-preinvoice.component.html',
  styleUrls: ['./dedicate-preinvoice.component.scss']
})
export class DedicatePreinvoiceComponent extends BaseComponent {
  linkToken: string;
  ready = false; // the token has been checked
  preinvoice: any; // null when the link leads nowhere
  pdf: Blob; // the original, kept for the download
  pdfState: 'loading' | 'shown' | 'failed' = 'loading';
  answerModal: NgbModalRef; // open while the approval is confirmed or the reason asked
  reason = '';
  error: string;
  done: 'APPROVED' | 'REJECTED';

  private pdfDocument: any; // the pdf.js document
  private pagesElement: HTMLElement;
  // The pages box exists only once the preinvoice is in, so drawing waits for
  // whichever of the two comes last
  @ViewChild('pages') set pages(ref: ElementRef<HTMLElement>) {
    this.pagesElement = ref?.nativeElement;
    this.draw();
  }

  constructor(
    public service: BaseService,
    public translator: TranslateService,
    public cookieService: CookieService,
    public titleService: Title,
    public route: ActivatedRoute,
    public modalService: NgbModal,) {
    super(service, translator, cookieService, titleService);
  }

  async ngOnInit() {
    await super.ngOnInit();
    this.translator.get('DEDICATE_PREINVOICE').subscribe(title => this.pageTitle = title);
    this.linkToken = this.route.snapshot.paramMap.get('token');
    try {
      const result = await this.service.apiCall('api/dedicate/preinvoice/check', { token: this.linkToken }).toPromise();
      this.preinvoice = result.valid ? result.preinvoice : null;
    } catch (error) {
      this.preinvoice = null;
      this.error = this.translator.instant(error.status == 429 ? 'DEDICATE_PREINVOICE_TOO_MANY' : 'DEDICATE_PREINVOICE_FAILED');
    }
    this.ready = true;
    if (this.preinvoice) {
      this.load();
    }
  }

  // pdf.js is imported here and not at the top, so it is fetched only by this page
  private async load() {
    try {
      const buffer: ArrayBuffer = await this.service.apiCall('api/dedicate/preinvoice/pdf', { token: this.linkToken }, undefined, { responseType: 'arraybuffer' }).toPromise();
      this.pdf = new Blob([buffer], { type: 'application/pdf' });
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
      pdfjs.GlobalWorkerOptions.workerSrc = 'assets/pdfjs/pdf.worker.min.js';
      // The worker takes the buffer it is given away, hence the copy
      this.pdfDocument = await pdfjs.getDocument({
        data: new Uint8Array(buffer.slice(0)),
        cMapUrl: 'assets/pdfjs/cmaps/',
        cMapPacked: true,
        standardFontDataUrl: 'assets/pdfjs/standard_fonts/',
      }).promise;
      await this.draw();
    } catch (error) {
      console.error(error);
      this.pdfState = 'failed';
    }
  }

  // Each page is drawn at the box's width times the screen's density, so it is
  // sharp on a phone; the canvas is then shrunk back to the box by css
  private async draw() {
    if (!this.pdfDocument || !this.pagesElement || this.pdfState != 'loading') {
      return;
    }
    this.pdfState = 'shown';
    try {
      const width = this.pagesElement.clientWidth;
      const density = Math.min(window.devicePixelRatio || 1, 3);
      for (let number = 1; number <= this.pdfDocument.numPages; number++) {
        const page = await this.pdfDocument.getPage(number);
        const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width * density });
        const canvas = document.createElement('canvas');
        // pdf.js places each glyph where it starts; under the page's rtl the
        // canvas would place it where it ends, and the text falls apart
        canvas.dir = 'ltr';
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        this.pagesElement.appendChild(canvas);
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      }
    } catch (error) {
      console.error(error);
      this.pdfState = 'failed';
    }
  }

  // With the pdf in hand it is saved at once; otherwise it is asked for again,
  // and the tab is opened before the request, while the tap still counts as a
  // user gesture, or the popup blocker eats it.
  async openPdf() {
    const name = `${this.translator.instant('DEDICATE_PREINVOICE')}-${this.preinvoice.no}.pdf`;
    if (this.pdf) {
      this.save(window.URL.createObjectURL(this.pdf), name);
      return;
    }
    const tab = window.open('', '_blank');
    this.error = null;
    try {
      const blob: Blob = await this.service.apiCall('api/dedicate/preinvoice/pdf', { token: this.linkToken }, undefined, { responseType: 'blob' }).toPromise();
      const url = window.URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        this.save(url, name);
      }
    } catch (error) {
      tab?.close();
      this.error = this.translator.instant(error.status == 429 ? 'DEDICATE_PREINVOICE_TOO_MANY' : 'DEDICATE_PREINVOICE_FAILED');
    }
  }

  private save(url: string, name: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  }

  // Both answers go through a modal, so a thumb scrolling the pages cannot
  // approve by brushing a button
  openAnswer(content: TemplateRef<any>) {
    this.error = null;
    this.answerModal = this.modalService.open(content, { centered: true, windowClass: 'samim' });
    this.answerModal.result.finally(() => {
      this.answerModal = null;
      this.error = null;
    }).catch(() => { });
  }

  async answer(approved: boolean) {
    if (!approved && this.reason.trim().length < 3) {
      this.error = this.translator.instant('DEDICATE_PREINVOICE_REASON_REQUIRED');
      return;
    }
    this.error = null;
    this.loading = true;
    try {
      await this.service.apiCall('api/dedicate/preinvoice/answer', { token: this.linkToken, approved, reason: approved ? undefined : this.reason }).toPromise();
      this.done = approved ? 'APPROVED' : 'REJECTED';
      this.answerModal?.close();
    } catch (error) {
      if (error.status == 404) {
        this.preinvoice = null; // answered or replaced meanwhile
        this.answerModal?.close();
      } else {
        this.error = this.translator.instant(error.status == 429 ? 'DEDICATE_PREINVOICE_TOO_MANY' : error.status == 400 ? 'DEDICATE_PREINVOICE_REASON_REQUIRED' : 'DEDICATE_PREINVOICE_FAILED');
      }
    }
    this.loading = false;
  }

}
